import React, {createContext, useContext, useReducer, useCallback} from 'react';
import PropTypes from 'prop-types';
import {SCREENS, mapPageStateToScreen} from '../constants/documentCollection';
import {mergeWithPendingUploads} from '../utils/documentHelpers';

const DocumentCollectionContext = createContext();

const initialState = {
  token: null,
  pageState: null,
  customerName: '',
  policyNumber: '',
  licensePlate: '',
  protectionNumber: '',
  yearsRequired: [],
  statuses: {},
  authMethods: [],
  maskedPhone: '',
  uploadConfig: null,
  futureCancellationDaysLeft: null,
  currentScreen: SCREENS.LOADING,
  loading: false,
  error: null,
  uploadedFiles: {},
  uploadingDocs: {},
  insuranceHistoryPath: null,
  idIssuanceDateProvided: false,
};

function reducer(state, action) {
  switch (action.type) {
  case 'SET_TOKEN_DATA':
    return {
      ...state,
      token: action.payload.token,
      pageState: action.payload.pageState || null,
      customerName: action.payload.customerName || '',
      policyNumber: action.payload.policyNumber || '',
      licensePlate: action.payload.licensePlate || '',
      protectionNumber: action.payload.protectionNumber || '',
      yearsRequired: action.payload.yearsRequired || [],
      statuses: action.payload.statuses
        ? mergeWithPendingUploads(action.payload.statuses, action.payload.token)
        : {},
      authMethods: action.payload.authMethods || [],
      maskedPhone: action.payload.maskedPhone || '',
      uploadConfig: action.payload.uploadConfig || null,
      futureCancellationDaysLeft: action.payload.futureCancellationDaysLeft ?? null,
      idIssuanceDateProvided: action.payload.idIssuanceDateProvided || false,
      insuranceHistoryPath: action.payload.idIssuanceDateProvided ? 'har_habituch' : null,
      currentScreen: action.payload.pageState
        ? mapPageStateToScreen(action.payload.pageState, action.payload.token)
        : SCREENS.WELCOME,
      loading: false,
    };
  case 'SET_SCREEN':
    return {...state, currentScreen: action.payload};
  case 'SET_LOADING':
    return {...state, loading: action.payload};
  case 'SET_ERROR':
    return {...state, error: action.payload, currentScreen: SCREENS.ERROR};
  case 'SET_UPLOADED_FILE': {
    const existing = state.uploadedFiles[action.payload.documentType] || [];
    return {
      ...state,
      uploadedFiles: {
        ...state.uploadedFiles,
        [action.payload.documentType]: [...existing, action.payload.fileData],
      },
    };
  }
  case 'REMOVE_UPLOADED_FILE': {
    const {documentType, fileIndex} = action.payload;
    const current = state.uploadedFiles[documentType] || [];
    const updated = current.filter((_, i) => i !== fileIndex);
    return {
      ...state,
      uploadedFiles: {
        ...state.uploadedFiles,
        [documentType]: updated,
      },
    };
  }
  case 'SET_STATUSES':
    return {
      ...state,
      statuses: action.payload,
    };
  case 'SET_DOCUMENT_STATUS':
    return {
      ...state,
      statuses: {
        ...state.statuses,
        [action.payload.documentType]: {
          ...state.statuses[action.payload.documentType] || {},
          status: action.payload.status,
          ...action.payload.files !== undefined ? {files: action.payload.files} : {},
        },
      },
    };
  case 'SET_DOC_UPLOADING':
    return {
      ...state,
      uploadingDocs: {
        ...state.uploadingDocs,
        [action.payload.documentType]: action.payload.uploading,
      },
    };
  case 'SET_INSURANCE_HISTORY_PATH':
    return {...state, insuranceHistoryPath: action.payload};
  case 'SET_ID_ISSUANCE_DATE_PROVIDED':
    return {...state, idIssuanceDateProvided: action.payload, insuranceHistoryPath: 'har_habituch'};
  default:
    return state;
  }
}

export function DocumentCollectionProvider({children}) {
  const [state, dispatch] = useReducer(reducer, initialState);

  const setTokenData = useCallback((token, data) => {
    dispatch({type: 'SET_TOKEN_DATA', payload: {token, ...data}});
  }, []);

  const setScreen = useCallback((screen) => {
    dispatch({type: 'SET_SCREEN', payload: screen});
  }, []);

  const setLoading = useCallback((loading) => {
    dispatch({type: 'SET_LOADING', payload: loading});
  }, []);

  const setError = useCallback((error) => {
    dispatch({type: 'SET_ERROR', payload: error});
  }, []);

  const setUploadedFile = useCallback((documentType, fileData) => {
    dispatch({type: 'SET_UPLOADED_FILE', payload: {documentType, fileData}});
  }, []);

  const removeUploadedFile = useCallback((documentType, fileIndex) => {
    dispatch({type: 'REMOVE_UPLOADED_FILE', payload: {documentType, fileIndex}});
  }, []);

  const setStatuses = useCallback((statuses) => {
    dispatch({type: 'SET_STATUSES', payload: statuses});
  }, []);

  const setDocumentStatus = useCallback((documentType, status, files) => {
    dispatch({type: 'SET_DOCUMENT_STATUS', payload: {documentType, status, files}});
  }, []);

  const setDocUploading = useCallback((documentType, uploading) => {
    dispatch({type: 'SET_DOC_UPLOADING', payload: {documentType, uploading}});
  }, []);

  const setInsuranceHistoryPath = useCallback((path) => {
    dispatch({type: 'SET_INSURANCE_HISTORY_PATH', payload: path});
  }, []);

  const setIdIssuanceDateProvided = useCallback((provided) => {
    dispatch({type: 'SET_ID_ISSUANCE_DATE_PROVIDED', payload: provided});
  }, []);

  const value = {
    ...state,
    setTokenData,
    setScreen,
    setLoading,
    setError,
    setUploadedFile,
    removeUploadedFile,
    setStatuses,
    setDocumentStatus,
    setDocUploading,
    setInsuranceHistoryPath,
    setIdIssuanceDateProvided,
  };

  return (
    <DocumentCollectionContext.Provider value={value}>
      {children}
    </DocumentCollectionContext.Provider>
  );
}

DocumentCollectionProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export function useDocumentCollection() {
  const context = useContext(DocumentCollectionContext);
  if (!context) {
    throw new Error('useDocumentCollection must be used within DocumentCollectionProvider');
  }
  return context;
}

export {SCREENS, mapPageStateToScreen};
export default DocumentCollectionContext;
